import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import SEOHead from '@/components/SEOHead';
import ProductCard from '@/components/ProductCard';
import { Truck, ShieldCheck, MessageCircle, Star, ArrowRight, PawPrint, Sparkles, Heart, MapPin } from 'lucide-react';

const MASCOT_IMG = "/mascot.png";

export default function HomePage() {
    const [categories, setCategories] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [catRes, prodRes] = await Promise.all([
                    api.get('/categories'),
                    api.get('/products?featured=true&limit=8'),
                ]);
                setCategories(catRes.data);
                setFeatured(prodRes.data.products || []);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
    }, []);

    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": "PetStore",
        "@id": "https://cloudpet.com.ar",
        "name": "CloudPet",
        "description": "Tienda online de juguetes, alimentos y accesorios para mascotas en Buenos Aires, Argentina. Envios rapidos a Capital Federal y Gran Buenos Aires.",
        "url": "https://cloudpet.com.ar",
        "logo": "https://cloudpet.com.ar/logo.png",
        "image": "https://cloudpet.com.ar/logo.png",
        "email": "info@cloudpet.com.ar",
        "address": {
            "@type": "PostalAddress",
            "addressLocality": "Buenos Aires",
            "addressRegion": "Ciudad Autónoma de Buenos Aires",
            "addressCountry": "AR"
        },
        "areaServed": [
            { "@type": "City", "name": "Buenos Aires" },
            { "@type": "AdministrativeArea", "name": "Gran Buenos Aires" }
        ],
        "priceRange": "$$",
        "currenciesAccepted": "ARS",
        "paymentAccepted": "Efectivo, Transferencia bancaria, Tarjeta de crédito",
        "hasMap": "https://maps.google.com/?q=Buenos+Aires,Argentina",
        "sameAs": []
    };

    return (
        <>
            <SEOHead
                title="Juguetes y Accesorios para Mascotas"
                description="CloudPet: Tu tienda online de juguetes, alimentos y accesorios para mascotas en Buenos Aires. Encontra todo lo que tu perro o gato necesita."
                url="https://cloudpet.com.ar/"
                structuredData={localBusinessSchema}
            />

            {/* Hero */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                {/* Layered background */}
                <div className="absolute inset-0 hero-gradient" />
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {/* Floating paw prints */}
                    <div className="absolute top-[10%] left-[5%] text-cp-primary/10 animate-float-slow"><PawPrint size={48} /></div>
                    <div className="absolute top-[25%] right-[8%] text-cp-secondary/8 animate-float-delayed"><PawPrint size={36} /></div>
                    <div className="absolute bottom-[20%] left-[15%] text-cp-primary/6 animate-float-slow"><PawPrint size={28} /></div>
                    <div className="absolute top-[60%] right-[20%] text-cp-accent-dark/10 animate-float-delayed"><Heart size={24} /></div>
                    <div className="absolute top-[15%] right-[35%] text-cp-warning/10 animate-float-slow"><Sparkles size={20} /></div>
                    {/* Decorative blobs */}
                    <div className="absolute -top-32 -right-32 w-96 h-96 bg-cp-primary/5 rounded-full blur-3xl" />
                    <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-cp-secondary/5 rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
                    {/* ─── LAYOUT: flex-row en mobile, grid en desktop ─── */}
                    <div className="flex flex-row items-center gap-3 lg:grid lg:grid-cols-12 lg:gap-4 lg:items-center lg:min-h-[540px]">

                        {/* Text content */}
                        <div className="flex-1 min-w-0 lg:col-span-6 xl:col-span-5 animate-fade-in z-10">
                            {/* Badge */}
                            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-cp-brand px-3 py-1.5 lg:px-4 lg:py-2 rounded-full text-xs lg:text-sm font-bold mb-3 lg:mb-6 shadow-sm border border-cp-primary/20">
                                <span className="flex h-2 w-2 relative">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cp-success opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-cp-success"></span>
                                </span>
                                Tienda online de mascotas
                            </div>

                            {/* Title */}
                            <h1 className="text-[1.7rem] sm:text-4xl lg:text-[3.5rem] xl:text-6xl font-heading font-black text-cp-brand leading-[1.1] tracking-tight mb-2 lg:mb-5">
                                Bienvenido a{' '}
                                <span className="relative inline-block">
                                    CloudPet
                                    <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 12" fill="none"><path d="M2 8c40-6 80-6 120-3 20 1.5 40 3 76 1" stroke="#6EC6F4" strokeWidth="3" strokeLinecap="round"/></svg>
                                </span>
                            </h1>

                            {/* Description */}
                            <p className="text-cp-text-body text-xs lg:text-lg leading-relaxed mb-4 lg:mb-8 max-w-md">
                                Juguetes, alimentos y accesorios. Envío rápido a todo Buenos Aires.
                            </p>

                            {/* CTAs — stacked en mobile, row en desktop */}
                            <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:gap-3 mb-4 lg:mb-8">
                                <Link
                                    to="/productos"
                                    data-testid="hero-cta-products"
                                    className="group inline-flex items-center justify-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-5 py-2.5 lg:px-7 lg:py-3.5 rounded-xl lg:rounded-full font-bold text-sm transition-all active:scale-95 shadow-lg shadow-cp-secondary/25"
                                >
                                    Ver Productos <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                                </Link>
                                <Link
                                    to="/productos?category=juguetes-perros"
                                    data-testid="hero-cta-secondary"
                                    className="inline-flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-cp-brand px-5 py-2.5 lg:px-7 lg:py-3.5 rounded-xl lg:rounded-full font-bold text-sm transition-all border border-cp-primary/20 shadow-sm"
                                >
                                    <PawPrint size={14} /> Juguetes para Perros
                                </Link>
                                <Link
                                    to="/locales"
                                    data-testid="hero-cta-locales"
                                    className="inline-flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-cp-brand px-5 py-2.5 lg:px-7 lg:py-3.5 rounded-xl lg:rounded-full font-bold text-sm transition-all border border-cp-primary/20 shadow-sm"
                                >
                                    <MapPin size={14} /> Peluquerías cerca tuyo
                                </Link>
                            </div>

                            {/* Trust badges — solo desktop */}
                            <div className="hidden lg:flex flex-wrap items-center gap-4 lg:gap-6">
                                {[
                                    { icon: Truck, text: 'Envio rapido' },
                                    { icon: ShieldCheck, text: 'Calidad garantizada' },
                                    { icon: MessageCircle, text: 'Atención WhatsApp' },
                                ].map((b, i) => (
                                    <div key={i} className="flex items-center gap-1.5 text-cp-text-muted">
                                        <b.icon size={14} className="text-cp-primary" />
                                        <span className="text-xs font-medium">{b.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Mascot */}
                        <div className="w-36 sm:w-48 lg:w-auto flex-shrink-0 lg:flex-shrink-1 relative lg:flex lg:items-center lg:justify-center lg:col-span-6 xl:col-span-7 animate-fade-in stagger-2">
                            {/* Glow */}
                            <div className="absolute w-[160px] h-[160px] sm:w-[220px] sm:h-[220px] lg:w-[480px] lg:h-[480px] rounded-full bg-gradient-to-br from-cp-primary-soft via-cp-secondary-soft to-cp-accent opacity-60 blur-xl" />
                            <div className="absolute w-[140px] h-[140px] sm:w-[200px] sm:h-[200px] lg:w-[420px] lg:h-[420px] rounded-full border-2 border-cp-primary/10 animate-spin-very-slow" />

                            {/* "Pedí por WA" — mobile */}
                            <div className="absolute -top-2 -right-1 bg-white rounded-xl px-2 py-1 shadow-md border border-cp-border-soft z-20 flex items-center gap-1 lg:hidden">
                                <span className="text-[11px]">🛒</span>
                                <span className="text-[10px] font-bold text-cp-brand whitespace-nowrap">Pedí por WA</span>
                            </div>

                            {/* Mascot image */}
                            <img
                                src={MASCOT_IMG}
                                alt="Mascota CloudPet saludando"
                                className="relative z-10 w-full lg:w-[400px] xl:w-[440px] h-auto drop-shadow-2xl mascot-hover"
                            />

                            {/* Floating badges — solo desktop */}
                            <div className="absolute top-4 right-4 lg:top-8 lg:right-8 bg-white rounded-2xl px-4 py-2.5 shadow-lg border border-cp-border-soft z-20 animate-float-slow hidden lg:flex items-center gap-2">
                                <div className="w-8 h-8 bg-cp-warning/10 rounded-full flex items-center justify-center">
                                    <Star size={14} className="text-cp-warning fill-cp-warning" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-cp-text-main">+500 Productos</p>
                                    <p className="text-[10px] text-cp-text-muted">Catalogo completo</p>
                                </div>
                            </div>

                            <div className="absolute bottom-8 left-0 lg:bottom-12 lg:left-4 bg-white rounded-2xl px-4 py-2.5 shadow-lg border border-cp-border-soft z-20 animate-float-delayed hidden lg:flex items-center gap-2">
                                <div className="w-8 h-8 bg-cp-success/10 rounded-full flex items-center justify-center">
                                    <Truck size={14} className="text-cp-success" />
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-cp-text-main">Envio a todo CABA</p>
                                    <p className="text-[10px] text-cp-text-muted">y alrededores</p>
                                </div>
                            </div>

                            <div className="absolute top-1/2 -left-2 lg:left-0 bg-white rounded-full p-2.5 shadow-lg border border-cp-border-soft z-20 animate-float-slow hidden lg:block">
                                <Heart size={16} className="text-cp-error fill-cp-error" />
                            </div>
                        </div>
                    </div>

                    {/* Trust cards — solo mobile */}
                    <div className="flex lg:hidden gap-2 mt-4">
                        {[
                            { Icon: Truck, label: 'Envío rápido' },
                            { Icon: ShieldCheck, label: 'Calidad garantizada' },
                            { Icon: MessageCircle, label: 'WhatsApp' },
                        ].map(({ Icon, label }, i) => (
                            <div key={i} className="flex-1 bg-white/70 backdrop-blur-sm border border-cp-border rounded-xl p-2 text-center">
                                <div className="flex justify-center text-cp-primary mb-1"><Icon size={15} /></div>
                                <div className="text-[10px] font-bold text-cp-text-muted leading-tight">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="bg-white py-16 lg:py-20" data-testid="categories-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10 lg:mb-14">
                        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-cp-text-main mb-3">Categorias</h2>
                        <p className="text-cp-text-body text-base lg:text-lg max-w-md mx-auto">Encontra lo mejor para tu mascota en cada categoria</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((cat, i) => (
                            <Link
                                key={cat.id}
                                to={`/productos?category=${cat.slug}`}
                                data-testid={`category-card-${cat.slug}`}
                                className={`group relative rounded-2xl overflow-hidden border border-cp-border hover:border-cp-primary transition-all animate-fade-in stagger-${i + 1}`}
                            >
                                <div className="aspect-[4/3] overflow-hidden">
                                    {cat.image ? (
                                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                                    ) : (
                                        <div className="w-full h-full bg-cp-surface-soft" />
                                    )}
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3 className="font-heading font-bold text-white text-sm lg:text-base">{cat.name}</h3>
                                    <p className="text-white/80 text-xs mt-1 hidden sm:block">{cat.description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="bg-cp-bg-alt py-16 lg:py-20" data-testid="featured-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-10 lg:mb-14">
                        <div>
                            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-cp-text-main mb-2">Productos Destacados</h2>
                            <p className="text-cp-text-body text-base">Los favoritos de nuestros clientes</p>
                        </div>
                        <Link to="/productos" className="hidden sm:inline-flex items-center gap-1 text-cp-secondary hover:text-cp-secondary-hover font-semibold text-sm transition-colors">
                            Ver todos <ArrowRight size={16} />
                        </Link>
                    </div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {[1,2,3,4].map(i => (
                                <div key={i} className="bg-white rounded-2xl h-72 skeleton" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {featured.map(product => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    )}
                    <div className="sm:hidden mt-8 text-center">
                        <Link to="/productos" className="inline-flex items-center gap-1 text-cp-secondary font-semibold text-sm">
                            Ver todos los productos <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="bg-white py-16 lg:py-20" data-testid="benefits-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {[
                            { icon: Truck, title: 'Envio Rapido', desc: 'A todo Buenos Aires y alrededores' },
                            { icon: ShieldCheck, title: 'Calidad Garantizada', desc: 'Productos seleccionados' },
                            { icon: MessageCircle, title: 'Atencion por WhatsApp', desc: 'Respuesta inmediata' },
                            { icon: Star, title: 'Los Mejores Precios', desc: 'Ofertas y descuentos' },
                        ].map((item, i) => (
                            <div key={i} className={`text-center p-6 rounded-2xl cp-gradient-warm animate-fade-in stagger-${i + 1}`}>
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-sm mb-4">
                                    <item.icon size={22} className="text-cp-primary" />
                                </div>
                                <h3 className="font-heading font-bold text-cp-text-main text-sm mb-1">{item.title}</h3>
                                <p className="text-cp-text-muted text-xs">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SEO Content */}
            <section className="bg-cp-bg-alt py-12 lg:py-16" data-testid="seo-content">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-xl sm:text-2xl font-heading font-bold text-cp-text-main mb-4">
                        Tu Pet Shop Online en Buenos Aires
                    </h2>
                    <p className="text-cp-text-body text-sm leading-relaxed mb-4">
                        CloudPet es tu tienda online de mascotas en Buenos Aires, Argentina. Ofrecemos una amplia variedad de juguetes para perros y gatos, alimentos premium y accesorios de la mejor calidad. Hacemos envios rapidos a Capital Federal, GBA y todo el pais. Compra facil y segura, coordinamos tu pedido por WhatsApp.
                    </p>
                    <p className="text-cp-text-muted text-xs">
                        Juguetes para perros | Juguetes para gatos | Alimento premium para mascotas | Collares y correas | Camas para perros | Accesorios para mascotas | Pet shop online Buenos Aires
                    </p>
                </div>
            </section>
        </>
    );
}
